import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  HardDrives, Package, Archive, Plus, Eye, ArrowClockwise, UploadSimple, Trash,
  MagnifyingGlass,
} from "@phosphor-icons/react";

const DEFAULT_TYPES = [
  { value: "opensips", label: "OpenSIPS" },
  { value: "freeswitch", label: "FreeSWITCH" },
  { value: "pd", label: "PD Engine" },
  { value: "ai_engine", label: "AI Engine" },
  { value: "webrtc", label: "WebRTC Server" },
];

const EMPTY_INSTALL = {
  server_type: "opensips",
  host: "",
  port: "22",
  username: "",
  password: "",
  config: {},
};

function defaultsFromFields(fields, host) {
  const cfg = {};
  (fields || []).forEach((f) => {
    if (f.default_from === "host" && host) cfg[f.key] = host;
    else if (f.default != null && f.default !== "") cfg[f.key] = String(f.default);
    else cfg[f.key] = "";
  });
  return cfg;
}

function formatBytes(n) {
  const x = Number(n) || 0;
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  return `${(x / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTs(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function cleanLogLine(s) {
  return String(s || "")
    .replace(/\u001b\[[0-9;]*[A-Za-z]/g, "")
    .replace(/\u001b\].*?(?:\u0007|\u001b\\)/g, "")
    .trim();
}

function daysAgo(n) {
  return Date.now() - n * 24 * 60 * 60 * 1000;
}

function installedSince(iso, sinceMs) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t >= sinceMs;
}

export default function Installation() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || can(user, "installation", "edit") || can(user, "installation", "add");
  const canDelete = user?.role === "admin" || can(user, "installation", "delete");
  const [tab, setTab] = useState("servers");
  const [serverTypes, setServerTypes] = useState(DEFAULT_TYPES);
  const [servers, setServers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [archive, setArchive] = useState([]);
  const [localBundles, setLocalBundles] = useState({});
  const [installersRoot, setInstallersRoot] = useState("/root/Installers");
  const [loading, setLoading] = useState(true);
  const [busyServerId, setBusyServerId] = useState(null);

  const [installOpen, setInstallOpen] = useState(false);
  const [installForm, setInstallForm] = useState(EMPTY_INSTALL);
  const [installFields, setInstallFields] = useState([]);
  const [installFieldsMap, setInstallFieldsMap] = useState({});
  const [installing, setInstalling] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState("opensips");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewServer, setViewServer] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [upgradePw, setUpgradePw] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [busyPkgId, setBusyPkgId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncTarget, setSyncTarget] = useState(null);
  const [syncPw, setSyncPw] = useState("");
  const [syncLines, setSyncLines] = useState(null);

  const [jobOpen, setJobOpen] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const logEndRef = useRef(null);

  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");

  const monthSince = useMemo(() => daysAgo(30), []);

  const typeMonthCounts = useMemo(() => {
    const map = {};
    for (const t of serverTypes) map[t.value] = 0;
    for (const row of servers) {
      const st = row.server_type;
      if (!(st in map)) map[st] = 0;
      if (installedSince(row.installed_at, monthSince)) map[st] += 1;
    }
    return map;
  }, [servers, serverTypes, monthSince]);

  const typeTotals = useMemo(() => {
    const map = {};
    for (const t of serverTypes) map[t.value] = 0;
    for (const row of servers) {
      const st = row.server_type;
      if (!(st in map)) map[st] = 0;
      map[st] += 1;
    }
    return map;
  }, [servers, serverTypes]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    for (const row of servers) {
      if (row.status) set.add(String(row.status));
    }
    return ["all", ...Array.from(set).sort()];
  }, [servers]);

  const filteredServers = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    return servers.filter((row) => {
      if (filterType !== "all" && row.server_type !== filterType) return false;
      if (filterStatus !== "all" && (row.status || "") !== filterStatus) return false;
      if (filterPeriod === "30d" && !installedSince(row.installed_at, monthSince)) return false;
      if (q) {
        const hay = [
          row.server_type_label, row.server_type, row.host, row.username,
          row.package_version_label, row.pool_name, row.status,
        ].map((x) => String(x || "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [servers, filterType, filterStatus, filterPeriod, filterSearch, monthSince]);

  const filteredInstallers = useMemo(() => {
    if (filterType === "all") return installers;
    return installers.filter((r) => r.server_type === filterType);
  }, [installers, filterType]);

  const filteredArchive = useMemo(() => {
    if (filterType === "all") return archive;
    return archive.filter((r) => r.server_type === filterType);
  }, [archive, filterType]);

  const clearFilters = () => {
    setFilterType("all");
    setFilterStatus("all");
    setFilterPeriod("all");
    setFilterSearch("");
  };

  const filtersActive = filterType !== "all" || filterStatus !== "all"
    || filterPeriod !== "all" || !!filterSearch.trim();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/installation");
      setServers(data.servers || []);
      setInstallers(data.installers || []);
      setArchive(data.archive || []);
      if (Array.isArray(data.server_types) && data.server_types.length) {
        setServerTypes(data.server_types);
      }
      if (data.install_fields && typeof data.install_fields === "object") {
        setInstallFieldsMap(data.install_fields);
      }
      if (data.local_bundles && typeof data.local_bundles === "object") {
        setLocalBundles(data.local_bundles);
      }
      if (data.installers_root) setInstallersRoot(data.installers_root);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [job?.logs?.length]);

  const stopPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const watchJob = (jobId) => {
    stopPoll();
    const tick = async () => {
      try {
        const { data } = await api.get(`/installation/jobs/${jobId}`);
        setJob(data);
        if (data.status === "success" || data.status === "failed") {
          stopPoll();
          load();
          if (viewServer?.id) openView(viewServer.id, false);
        }
      } catch {
        /* keep polling until terminal or dialog closed */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 800);
  };

  const applyServerTypeFields = (serverType, host) => {
    const fields = installFieldsMap[serverType] || [];
    setInstallFields(fields);
    setInstallForm((f) => ({
      ...f,
      server_type: serverType,
      config: defaultsFromFields(fields, host || f.host),
    }));
  };

  const openInstall = () => {
    const st = "opensips";
    const fields = installFieldsMap[st] || [];
    setInstallFields(fields);
    setInstallForm({ ...EMPTY_INSTALL, config: defaultsFromFields(fields, "") });
    setInstallOpen(true);
    // Refresh field schema in case map was empty
    api.get(`/installation/fields/${st}`).then(({ data }) => {
      const fl = data.fields || [];
      setInstallFields(fl);
      setInstallFieldsMap((m) => ({ ...m, [st]: fl }));
      setInstallForm((f) => ({ ...f, config: defaultsFromFields(fl, f.host) }));
    }).catch(() => {});
  };

  const onInstallTypeChange = async (serverType) => {
    applyServerTypeFields(serverType, installForm.host);
    try {
      const { data } = await api.get(`/installation/fields/${serverType}`);
      const fl = data.fields || [];
      setInstallFields(fl);
      setInstallFieldsMap((m) => ({ ...m, [serverType]: fl }));
      setInstallForm((f) => ({
        ...f,
        server_type: serverType,
        config: defaultsFromFields(fl, f.host),
      }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const submitInstall = async () => {
    const host = (installForm.host || "").trim();
    const username = (installForm.username || "").trim();
    const password = installForm.password || "";
    const port = parseInt(installForm.port, 10);
    if (!installForm.server_type) { toast.error("Select a server"); return; }
    if (!host) { toast.error("Server IP is required"); return; }
    if (!Number.isFinite(port) || port < 1) { toast.error("Enter a valid port"); return; }
    if (!username) { toast.error("Username is required"); return; }
    if (!password) { toast.error("Password is required"); return; }

    const config = { ...(installForm.config || {}) };
    for (const f of installFields) {
      if (f.default_from === "host" && !config[f.key]) config[f.key] = host;
      if (f.required && !(config[f.key] || "").toString().trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    setInstalling(true);
    try {
      const { data } = await api.post("/installation/install", {
        server_type: installForm.server_type,
        host,
        port,
        username,
        password,
        config,
      });
      setInstallOpen(false);
      setJob(data);
      setJobOpen(true);
      watchJob(data.id);
      toast.success("Kickstart installation started");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setInstalling(false);
    }
  };

  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Choose a .zip file"); return; }
    if (!uploadType) { toast.error("Select a server type"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("server_type", uploadType);
      fd.append("file", uploadFile);
      await api.post("/installation/installers/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Installer uploaded — previous version moved to Archive");
      setUploadOpen(false);
      setUploadFile(null);
      load();
      setTab("installers");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setUploading(false);
    }
  };

  const openView = async (serverId, openDialog = true) => {
    setViewLoading(true);
    if (openDialog) {
      setViewOpen(true);
      setUpgradePw("");
    }
    try {
      const { data } = await api.get(`/installation/servers/${serverId}`);
      setViewServer(data.server);
      setViewDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      if (openDialog) setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const deletePackage = async (row) => {
    const label = `${row.server_type_label || row.server_type} ${row.version_label || ""}`.trim();
    if (!window.confirm(`Delete installer “${label}” (${row.filename})?`)) return;
    setBusyPkgId(row.id);
    try {
      await api.delete(`/installation/installers/${row.id}`);
      toast.success("Installer deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyPkgId(null);
    }
  };

  const deleteInstalledServer = async (row) => {
    const label = `${row.server_type_label || row.server_type} @ ${row.host}`;
    if (!window.confirm(
      `Remove “${label}” from Installation → Servers?\n\nThis only deletes the install record here — it does not uninstall software on the remote host.`,
    )) return;
    setBusyServerId(row.id);
    try {
      await api.delete(`/installation/servers/${row.id}`);
      toast.success("Server removed from Installation list");
      if (viewServer?.id === row.id) {
        setViewOpen(false);
        setViewServer(null);
        setViewDetail(null);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyServerId(null);
    }
  };

  const openSync = (row) => {
    setSyncTarget(row);
    setSyncPw("");
    setSyncLines(null);
    setSyncOpen(true);
  };

  const submitSync = async () => {
    if (!syncTarget?.id) return;
    setSyncingId(syncTarget.id);
    setSyncLines(null);
    try {
      const body = {};
      if ((syncPw || "").trim()) body.password = syncPw.trim();
      const { data } = await api.post(`/installation/servers/${syncTarget.id}/sync`, body);
      const lines = data.lines || [];
      setSyncLines(lines);
      toast.success(`Synced ${syncTarget.host} (${lines.length} step${lines.length === 1 ? "" : "s"})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSyncingId(null);
    }
  };

  const submitUpgrade = async () => {
    if (!viewServer?.id) return;
    if (!upgradePw) { toast.error("Password is required to upgrade"); return; }
    setUpgrading(true);
    try {
      const { data } = await api.post(
        `/installation/servers/${viewServer.id}/upgrade`,
        { password: upgradePw },
      );
      setViewOpen(false);
      setJob(data);
      setJobOpen(true);
      watchJob(data.id);
      toast.success("Upgrade started");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="installation-page">
      <PageHeader
        title="Installation"
        subtitle="Upload server installer packages, deploy to hosts, and upgrade to the latest release."
      >
        {canEdit && (
          <Button
            type="button"
            className="rounded-lg"
            onClick={openInstall}
            data-testid="installation-install-server"
          >
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Install Server
          </Button>
        )}
      </PageHeader>

      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
        data-testid="installation-type-cards"
      >
        {serverTypes.map((t) => {
          const month = typeMonthCounts[t.value] || 0;
          const total = typeTotals[t.value] || 0;
          const active = filterType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => {
                setFilterType(active ? "all" : t.value);
                setTab("servers");
              }}
              data-testid={`installation-card-${t.value}`}
              className={`text-left rounded-xl border bg-card card-shadow p-4 transition-colors ${
                active
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t.label}</p>
              <p className="text-2xl font-semibold tabular-nums mt-1" data-testid={`installation-card-${t.value}-month`}>
                {month}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                installed last 30 days
                <span className="text-muted-foreground/80"> · {total} total</span>
              </p>
            </button>
          );
        })}
      </div>

      <div
        className="flex flex-wrap items-end gap-3"
        data-testid="installation-filters"
      >
        <div className="space-y-1.5 flex-1 min-w-[180px] max-w-sm">
          <Label className="text-xs text-muted-foreground">Search</Label>
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              data-testid="installation-filter-search"
              className="h-9 rounded-lg pl-8"
              placeholder="Host, user, version, pool…"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5 min-w-[150px]">
          <Label className="text-xs text-muted-foreground">Server type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="installation-filter-type" className="h-9 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {serverTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 min-w-[140px]">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="installation-filter-status" className="h-9 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === "all" ? "All statuses" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 min-w-[150px]">
          <Label className="text-xs text-muted-foreground">Installed</Label>
          <Select value={filterPeriod} onValueChange={setFilterPeriod}>
            <SelectTrigger data-testid="installation-filter-period" className="h-9 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            className="h-9 rounded-lg"
            onClick={clearFilters}
            data-testid="installation-filters-clear"
          >
            Clear
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="installation-tabs">
          <TabsTrigger value="servers" data-testid="installation-tab-servers">
            <HardDrives className="w-4 h-4 mr-1.5" weight="duotone" /> Servers
            <span className="ml-1.5 text-xs tabular-nums text-muted-foreground">
              ({filteredServers.length})
            </span>
          </TabsTrigger>
          <TabsTrigger value="installers" data-testid="installation-tab-installers">
            <Package className="w-4 h-4 mr-1.5" weight="duotone" /> Installers
            <span className="ml-1.5 text-xs tabular-nums text-muted-foreground">
              ({filteredInstallers.length})
            </span>
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="installation-tab-archive">
            <Archive className="w-4 h-4 mr-1.5" weight="duotone" /> Archive
            <span className="ml-1.5 text-xs tabular-nums text-muted-foreground">
              ({filteredArchive.length})
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="servers">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : servers.length === 0 ? (
            <EmptyState
              testid="installation-servers-empty"
              icon={HardDrives}
              title="No installed servers"
              description="Use Install Server to deploy OpenSIPS, FreeSWITCH, PD, AI Engine, or WebRTC."
            />
          ) : filteredServers.length === 0 ? (
            <EmptyState
              testid="installation-servers-filtered-empty"
              icon={HardDrives}
              title="No servers match filters"
              description="Try clearing search or changing type / status / period."
            />
          ) : (
            <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Server type</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Port</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Pool name</TableHead>
                    <TableHead>Installed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServers.map((row) => (
                    <TableRow key={row.id} data-testid={`install-server-row-${row.id}`}>
                      <TableCell className="font-medium">
                        {row.server_type_label || row.server_type}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{row.host}</TableCell>
                      <TableCell className="tabular-nums">{row.port}</TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.package_version_label || "—"}</TableCell>
                      <TableCell>{row.pool_name || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatTs(row.installed_at)}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={row.status || "unknown"} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="rounded-lg h-8 w-8 p-0"
                            onClick={() => openView(row.id)}
                            data-testid={`install-server-view-${row.id}`}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && row.server_type === "freeswitch" && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="rounded-lg h-8 w-8 p-0"
                              disabled={syncingId === row.id}
                              onClick={() => openSync(row)}
                              data-testid={`install-server-sync-${row.id}`}
                              title="Sync FreeSWITCH (phones, WG, SSH/ESL)"
                            >
                              <ArrowClockwise
                                className={`w-4 h-4 ${syncingId === row.id ? "animate-spin" : ""}`}
                              />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive"
                              disabled={busyServerId === row.id}
                              onClick={() => deleteInstalledServer(row)}
                              data-testid={`install-server-delete-${row.id}`}
                              title="Delete"
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="installers">
          <div className="space-y-4">
            {canEdit && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  className="rounded-lg"
                  onClick={() => { setUploadFile(null); setUploadOpen(true); }}
                  data-testid="installation-upload"
                >
                  <UploadSimple className="w-4 h-4 mr-1.5" weight="bold" /> Upload installer
                </Button>
              </div>
            )}
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : installers.length === 0 ? (
              <EmptyState
                testid="installation-installers-empty"
                icon={Package}
                title="No current installers"
                description="Upload a .zip for each server type. New uploads auto-version and archive the previous file."
              />
            ) : filteredInstallers.length === 0 ? (
              <EmptyState
                testid="installation-installers-filtered-empty"
                icon={Package}
                title="No installers match filters"
                description="Clear the server-type filter or pick another type."
              />
            ) : (
              <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Server type</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Filename</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      {canEdit && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInstallers.map((row) => (
                      <TableRow key={row.id} data-testid={`installer-row-${row.id}`}>
                        <TableCell className="font-medium">
                          {row.server_type_label || row.server_type}
                        </TableCell>
                        <TableCell>{row.version_label}</TableCell>
                        <TableCell className="font-mono text-sm">{row.filename}</TableCell>
                        <TableCell className="tabular-nums">{formatBytes(row.size)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatTs(row.created_at)}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive"
                              disabled={busyPkgId === row.id}
                              onClick={() => deletePackage(row)}
                              data-testid={`installer-delete-${row.id}`}
                              title="Delete"
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="archive">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : archive.length === 0 ? (
            <EmptyState
              testid="installation-archive-empty"
              icon={Archive}
              title="No archived releases"
              description="When you upload a newer installer, the previous version appears here."
            />
          ) : filteredArchive.length === 0 ? (
            <EmptyState
              testid="installation-archive-filtered-empty"
              icon={Archive}
              title="No archives match filters"
              description="Clear the server-type filter or pick another type."
            />
          ) : (
            <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Server type</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Archived</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArchive.map((row) => (
                    <TableRow key={row.id} data-testid={`archive-row-${row.id}`}>
                      <TableCell className="font-medium">
                        {row.server_type_label || row.server_type}
                      </TableCell>
                      <TableCell>{row.version_label}</TableCell>
                      <TableCell className="font-mono text-sm">{row.filename}</TableCell>
                      <TableCell className="tabular-nums">{formatBytes(row.size)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatTs(row.archived_at || row.created_at)}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive"
                            disabled={busyPkgId === row.id}
                            onClick={() => deletePackage(row)}
                            data-testid={`archive-delete-${row.id}`}
                            title="Delete"
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Install Server modal — SSH destination + kickstart prompts */}
      <Dialog open={installOpen} onOpenChange={setInstallOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="install-server-dialog">
          <DialogHeader>
            <DialogTitle>Install Server</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-xs text-muted-foreground">
              Copies the kickstart bundle from <code className="text-xs">{installersRoot}</code> to the host and runs <code className="text-xs">kickstart.sh</code> with the values below (same prompts as a manual install).
            </p>
            <div className="space-y-1.5">
              <Label>Select server</Label>
              <Select
                value={installForm.server_type}
                onValueChange={onInstallTypeChange}
              >
                <SelectTrigger data-testid="install-server-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serverTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {localBundles[installForm.server_type] && (
                <p className={`text-xs ${localBundles[installForm.server_type].available ? "text-muted-foreground" : "text-destructive"}`}>
                  Bundle: <code className="text-xs">{localBundles[installForm.server_type].path}</code>
                  {localBundles[installForm.server_type].available ? " (ready)" : " (missing)"}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-border p-3 space-y-3">
              <p className="text-sm font-medium">SSH destination</p>
              <div className="space-y-1.5">
                <Label htmlFor="inst-host">Server IP</Label>
                <Input
                  id="inst-host"
                  data-testid="install-host"
                  value={installForm.host}
                  onChange={(e) => {
                    const host = e.target.value;
                    setInstallForm((f) => {
                      const next = { ...f, host };
                      const cfg = { ...(f.config || {}) };
                      installFields.forEach((field) => {
                        if (field.default_from === "host") cfg[field.key] = host;
                      });
                      return { ...next, config: cfg };
                    });
                  }}
                  placeholder="203.0.113.10"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="inst-port">SSH port</Label>
                  <Input
                    id="inst-port"
                    data-testid="install-port"
                    type="number"
                    min="1"
                    max="65535"
                    value={installForm.port}
                    onChange={(e) => setInstallForm((f) => ({ ...f, port: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inst-user">Username</Label>
                  <Input
                    id="inst-user"
                    data-testid="install-username"
                    value={installForm.username}
                    onChange={(e) => setInstallForm((f) => ({ ...f, username: e.target.value }))}
                    placeholder="root"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inst-pass">Password</Label>
                <Input
                  id="inst-pass"
                  data-testid="install-password"
                  type="password"
                  value={installForm.password}
                  onChange={(e) => setInstallForm((f) => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
            </div>
            {installFields.length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-3">
                <p className="text-sm font-medium">Kickstart configuration</p>
                {installFields.map((field) => (
                  <div className="space-y-1.5" key={field.key}>
                    <Label htmlFor={`cfg-${field.key}`}>
                      {field.label}
                      {field.required ? " *" : ""}
                    </Label>
                    <Input
                      id={`cfg-${field.key}`}
                      data-testid={`install-cfg-${field.key}`}
                      type={field.secret ? "password" : "text"}
                      value={installForm.config?.[field.key] ?? ""}
                      placeholder={field.placeholder || field.default || ""}
                      onChange={(e) => setInstallForm((f) => ({
                        ...f,
                        config: { ...(f.config || {}), [field.key]: e.target.value },
                      }))}
                      autoComplete="off"
                    />
                    {field.help ? (
                      <p className="text-xs text-muted-foreground">{field.help}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setInstallOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={installing}
              onClick={submitInstall}
              data-testid="install-submit"
            >
              {installing ? "Starting…" : "Start kickstart install"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload installer */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-installer-dialog">
          <DialogHeader>
            <DialogTitle>Upload installer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Server type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="upload-server-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serverTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="upload-file">ZIP package</Label>
              <Input
                id="upload-file"
                data-testid="upload-file"
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">
                Uploading replaces the current installer for this type and moves the old one to Archive with an auto version.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={uploading}
              onClick={submitUpload}
              data-testid="upload-submit"
            >
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View / upgrade */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="view-server-dialog">
          <DialogHeader>
            <DialogTitle>Installed server</DialogTitle>
          </DialogHeader>
          {viewLoading && !viewServer ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : viewServer ? (
            <div className="space-y-4 py-1">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Server type</dt>
                <dd className="font-medium">{viewServer.server_type_label}</dd>
                <dt className="text-muted-foreground">Host</dt>
                <dd className="font-mono">{viewServer.host}:{viewServer.port}</dd>
                <dt className="text-muted-foreground">Username</dt>
                <dd>{viewServer.username}</dd>
                <dt className="text-muted-foreground">Installed version</dt>
                <dd>{viewServer.package_version_label || "—"}</dd>
                <dt className="text-muted-foreground">Pool name</dt>
                <dd>{viewServer.pool_name || "—"}</dd>
                <dt className="text-muted-foreground">Latest package</dt>
                <dd>{viewDetail?.latest_package?.version_label || "—"}</dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd><StatusPill status={viewServer.status || "unknown"} /></dd>
              </dl>
              {canEdit && viewServer.server_type === "freeswitch" && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-sm font-medium">Sync FreeSWITCH config</p>
                  <p className="text-xs text-muted-foreground">
                    Re-apply WireGuard peer, inventory, SSH/ESL, and web-phone directory users (fixes Registration lost after reinstall).
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg w-full"
                    disabled={syncingId === viewServer.id}
                    onClick={() => openSync(viewServer)}
                    data-testid="view-server-sync"
                  >
                    <ArrowClockwise
                      className={`w-4 h-4 mr-1.5 ${syncingId === viewServer.id ? "animate-spin" : ""}`}
                    />
                    {syncingId === viewServer.id ? "Syncing…" : "Sync now"}
                  </Button>
                </div>
              )}
              {viewDetail?.can_upgrade && canEdit && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-sm font-medium">Upgrade to {viewDetail.latest_package.version_label}</p>
                  <Label htmlFor="upgrade-pw">Destination password</Label>
                  <Input
                    id="upgrade-pw"
                    data-testid="upgrade-password"
                    type="password"
                    value={upgradePw}
                    onChange={(e) => setUpgradePw(e.target.value)}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    className="rounded-lg w-full"
                    disabled={upgrading}
                    onClick={submitUpgrade}
                    data-testid="upgrade-submit"
                  >
                    <ArrowClockwise className="w-4 h-4 mr-1.5" />
                    {upgrading ? "Starting…" : "Upgrade"}
                  </Button>
                </div>
              )}
              {!viewDetail?.can_upgrade && viewDetail?.latest_package && (
                <p className="text-sm text-muted-foreground">Already on the latest package.</p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setViewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FreeSWITCH sync */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sync-server-dialog">
          <DialogHeader>
            <DialogTitle>Sync FreeSWITCH</DialogTitle>
          </DialogHeader>
          {syncTarget && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">
                Push CP config to{" "}
                <span className="font-mono text-foreground">
                  {syncTarget.username || "root"}@{syncTarget.host}:{syncTarget.port || 22}
                </span>
                : WireGuard peer, media/WebRTC inventory, SSH key + ESL password, and web-phone directory users.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="sync-pw">Destination password (optional)</Label>
                <Input
                  id="sync-pw"
                  data-testid="sync-password"
                  type="password"
                  value={syncPw}
                  onChange={(e) => setSyncPw(e.target.value)}
                  placeholder="Blank = System Settings SSH password"
                  autoComplete="new-password"
                />
              </div>
              {syncLines && (
                <div
                  className="max-h-48 overflow-y-auto rounded-lg border border-border bg-muted/40 p-3 font-mono text-xs space-y-1"
                  data-testid="sync-server-lines"
                >
                  {syncLines.map((line, i) => (
                    <div key={i} className="break-all whitespace-pre-wrap">{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => setSyncOpen(false)}
            >
              {syncLines ? "Close" : "Cancel"}
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={!syncTarget || syncingId === syncTarget?.id}
              onClick={submitSync}
              data-testid="sync-submit"
            >
              <ArrowClockwise
                className={`w-4 h-4 mr-1.5 ${syncingId === syncTarget?.id ? "animate-spin" : ""}`}
              />
              {syncingId === syncTarget?.id ? "Syncing…" : "Sync"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job progress / logs */}
      <Dialog
        open={jobOpen}
        onOpenChange={(o) => {
          setJobOpen(o);
          if (!o) stopPoll();
        }}
      >
        <DialogContent
          className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-hidden flex flex-col gap-4"
          data-testid="install-job-dialog"
        >
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle>
              {job?.kind === "upgrade" ? "Upgrade progress" : "Installation progress"}
            </DialogTitle>
          </DialogHeader>
          {job && (
            <div className="space-y-3 py-1 min-w-0 overflow-hidden flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-3 text-sm min-w-0">
                <span className="min-w-0 break-all">
                  {job.server_type_label} → {job.username}@{job.host}:{job.port}
                </span>
                <span className="tabular-nums font-medium shrink-0">{job.progress ?? 0}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden shrink-0">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${Math.min(100, job.progress || 0)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground capitalize shrink-0">
                Status: {job.status}
                {job.package_version_label ? ` · ${job.package_version_label}` : ""}
              </p>
              <div
                className="min-h-0 flex-1 h-48 max-h-[40vh] overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-muted/40 p-3 font-mono text-xs"
                data-testid="install-job-logs"
              >
                {(job.logs || []).map((l, i) => (
                  <div key={i} className="break-all whitespace-pre-wrap leading-relaxed">
                    <span className="text-muted-foreground">{formatTs(l.ts)} </span>
                    {cleanLogLine(l.line)}
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
            </div>
          )}
          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => { stopPoll(); setJobOpen(false); }}
            >
              {job?.status === "success" || job?.status === "failed" ? "Close" : "Hide"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
